/**
 * @file AddFooterDocDialog.tsx
 * @description Окно «+ Добавить ссылку на документ» блока «Ссылки» подвала
 * (phase19.16, партия «Юридические документы»; SB142; uiux-legal-steps-b-c
 * §8.2, uiux-legal-docs §5): поиск по названию, «Группы» и «Документы» с
 * видом и состоянием, «Все документы»; оферты, политики и условий возврата
 * нет (это ссылки платформы); уже стоящие в «Ссылках» неактивны с пометкой
 * «уже в «Ссылках»». Автоматической группы сертификатов нет — первой строкой
 * «+ Создать» (создаётся и публикуется по «Добавить»). Внизу [Отмена]
 * [Добавить]. На телефоне — во весь экран.
 *
 * Список — свежий с сервера при каждом открытии (`useLegalTargets`).
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import { ComboboxList } from "../../ui/combobox-list";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { FieldHint } from "../../ui/settings-groups";
import { useLegalTargets } from "../../../hooks/site-view/useLegalTargets";
import {
  ALL_DOCUMENTS_VALUE,
  CERTIFICATES_NO_RIGHT_HINT,
  CREATE_CERTIFICATES_VALUE,
  EMPTY_GROUP_HINT,
  isCertificatesGroup,
  isEmptyLegalGroup,
  legalTargetOptions,
} from "../../../lib/site-view/legal-targets";

/** Что добавить: документ или группа по id, либо «Все документы». */
export type FooterDocPick = { kind: "document"; documentId: string } | { kind: "allDocuments" };

interface Props {
  /** Уже в «Ссылках»: id документов и `ALL_DOCUMENTS_VALUE`. */
  taken: ReadonlySet<string>;
  onAdd: (pick: FooterDocPick) => void;
  onClose: () => void;
}

export function AddFooterDocDialog({ taken, onAdd, onClose }: Props) {
  const legal = useLegalTargets({ fresh: true });
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const { targets } = legal;
  const options = targets ? legalTargetOptions({ targets, canCreateCertificates: legal.canCreate, footer: true, taken }) : [];
  const pickedTarget = targets?.find((t) => t.id === picked) ?? null;
  const noCertificates = targets !== null && !targets.some(isCertificatesGroup);

  async function add() {
    if (picked === null) return;
    if (picked === ALL_DOCUMENTS_VALUE) return onAdd({ kind: "allDocuments" });
    if (picked === CREATE_CERTIFICATES_VALUE) {
      const created = await legal.createCertificates();
      if (created) onAdd({ kind: "document", documentId: created.id });
      return;
    }
    onAdd({ kind: "document", documentId: picked });
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Добавить ссылку на документ</DialogTitle>
          <DialogDescription>Ссылка встанет в строку внизу подвала, у сведений о продавце.</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col sm:max-h-[60vh]">
          {legal.failed ? (
            <div className="flex flex-col items-start gap-2">
              <p className="type-body text-on-surface-variant">Не удалось загрузить документы.</p>
              <Button variant="outline" onClick={legal.reload}>
                Повторить
              </Button>
            </div>
          ) : targets === null ? (
            <p className="type-body text-on-surface-variant">Загружаем документы…</p>
          ) : (
            <ComboboxList
              options={options}
              value={picked}
              query={query}
              onQueryChange={setQuery}
              onPick={setPicked}
              searchPlaceholder="Поиск по названию…"
              emptyText="Ничего не найдено. Документы создаются в «Юридических документах»."
              touch
            />
          )}
        </div>
        {pickedTarget && isEmptyLegalGroup(pickedTarget) ? <FieldHint className="mt-0">{EMPTY_GROUP_HINT}</FieldHint> : null}
        {noCertificates && !legal.canCreate ? <FieldHint className="mt-0">{CERTIFICATES_NO_RIGHT_HINT}</FieldHint> : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={legal.creating}>
            Отмена
          </Button>
          <Button onClick={() => void add()} disabled={picked === null} loading={legal.creating}>
            Добавить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
